import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Title, useDataProvider, useGetIdentity, useGetList, useNotify } from 'react-admin';
import TpaTransitionForm from './TpaTransitionForm';
import {
  CleanupRule,
  ClientDataType,
  ClientSplitRule,
  ClientSplitRulePayload,
  initialTpaFormData,
  TpaForClient,
  TpaFormData,
  CleanupRequestPayload,
} from '../utils/DataTypes';
import { truncateClientName } from './clientSplitHelpers';
import { CLIENT_SPLIT_STRINGS, DEFAULTS, MEMBER_SUMMARY_STRINGS, splitTypeOptions } from '../constants';
import ClientCombineForm from './ClientCombineForm';
import ClientCleanupForm from './ClientCleanupForm';
import ClientSplitForm from './ClientSplitForm';

const ManageClientSplit = () => {
  const [selectedClient, setSelectedClient] = useState<ClientDataType | null>(null);
  const [splitType, setSplitType] = useState('tpa');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [tpaFormData, setTpaFormData] = useState<TpaFormData>(initialTpaFormData);
  const [combineClients, setCombineClients] = useState<ClientDataType[]>([]);
  const [cleanupRules, setCleanupRules] = useState<CleanupRule[]>([]);
  const [associatedTpa, setAssociatedTpa] = useState<TpaForClient | null>(null);
  const [tpaList, setTpaList] = useState<TpaForClient[]>([]);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();

  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchTpa = async () => {
      if (!selectedClient || splitType !== 'tpa') {
        setAssociatedTpa(null);
        return;
      }
      try {
        const { data: tpa } = await dataProvider.getOne('TpaForClient', {
          id: selectedClient.client_number,
        });
        if (tpa && Object.keys(tpa).length > 1) {
          setAssociatedTpa(tpa);
        } else {
          setAssociatedTpa(null);
          if (effectiveDate) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.NO_TPA_FOUND', { type: 'info' });
          }
        }
      } catch (error) {
        setAssociatedTpa(null);
        notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.ERROR_FETCHING_TPA', { type: 'info' });
      }
    };

    fetchTpa();
  }, [selectedClient, dataProvider, notify, splitType, effectiveDate]);

  useEffect(() => {
    if (selectedClient) {
      const truncatedName = truncateClientName(selectedClient.client_name);
      const defaultTpaName = associatedTpa ? associatedTpa.vendor_name : '';

      setTpaFormData({
        ...initialTpaFormData,
        clientName: truncatedName,
        clientNumber: selectedClient.client_number,
        oldTpaName: defaultTpaName,
        newTpaName: defaultTpaName,
        oldTpaBillingNumber: `${selectedClient.client_number}b`,
        newTpaBillingNumber: `${selectedClient.client_number}a`,
        newBillingClientName: truncatedName,
        gpAccountingClientId: '',
      });
      setCombineClients([]);
    } else {
      setTpaFormData(initialTpaFormData);
      setCombineClients([]);
    }
  }, [selectedClient, associatedTpa]);

  const { data: clientChoices = [], isLoading } = useGetList('clients', {
    filter: { report_type: 'Clients' },
    pagination: { page: 1, perPage: 5000 },
    sort: { field: 'client_name', order: 'ASC' },
  });

  const { data: tpaChoices = [], isLoading: isTpaLoading } = useGetList('Tpa', {
    pagination: { page: 1, perPage: 2000 },
    sort: { field: 'vendor_name', order: 'ASC' },
  });

  useEffect(() => {
    if (tpaChoices) {
      setTpaList(tpaChoices as TpaForClient[]);
    }
  }, [tpaChoices]);

  const handleTpaFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setTpaFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleTpaSelect = (fieldName: 'oldTpaName' | 'newTpaName', newValue: TpaForClient | null) => {
    setTpaFormData((prevState) => ({
      ...prevState,
      [fieldName]: newValue ? newValue.vendor_name : '',
    }));
  };

  const handleCleanupSave = async (payload: CleanupRequestPayload) => {
    if (!selectedClient || !effectiveDate) {
      notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.MISSING_CLIENT_OR_DATE', { type: 'info' });
      return;
    }

    setIsSaving(true);

    try {
      const res = await dataProvider.create('ClientCleanupConfig', {
        data: {
          ...payload,
          splitType: 'cleanup',
          createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      });

      if (res?.data?.success) {
        notify('notifications.CLIENT_CLEANUP.SAVED', { type: 'success' });
      } else {
        notify('notifications.CONFIG_FAILED', { type: 'error' });
      }
    } catch (error) {
      notify('notifications.CONFIG_FAILED', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (rulesFromChild?: CleanupRule[] | ClientSplitRulePayload[]) => {
    if (!selectedClient || !effectiveDate) {
      notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.MISSING_CLIENT_OR_DATE', { type: 'info' });
      return;
    }
    setIsSaving(true);
    let payload;
    try {
      switch (splitType) {
        case 'tpa':
          if (!tpaFormData.clientName?.trim() || !tpaFormData.oldTpaName || !tpaFormData.newTpaName) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.MISSING_TPA_FIELDS', { type: 'info' });
            setIsSaving(false);
            return;
          }

          if (tpaFormData.clientName.trim() === tpaFormData.newBillingClientName.trim()) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.SAME_CLIENT_ERROR', { type: 'info' });
            setIsSaving(false);
            return;
          }

          if (!tpaFormData.clientName.includes(' - ') || !tpaFormData.newBillingClientName.includes(' - ')) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.CLIENT_NAME_FORMAT', { type: 'info' });
            setIsSaving(false);
            return;
          }

          if (tpaFormData.oldTpaName === tpaFormData.newTpaName) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.VALIDATION.SAME_TPA_ERROR', { type: 'info' });
            setIsSaving(false);
            return;
          }
          payload = {
            originalClientNumber: selectedClient.client_number,
            splitType: splitType,
            effectiveDate: effectiveDate,
            details: {
              clientName: selectedClient.client_name,
              clientNumber: selectedClient.client_number,
              billingClientName: tpaFormData.clientName,
              oldTpaName: tpaFormData.oldTpaName,
              oldTpaBillingNumber: tpaFormData.oldTpaBillingNumber,
              newTpaName: tpaFormData.newTpaName,
              newTpaBillingNumber: tpaFormData.newTpaBillingNumber,
              newBillingClientName: tpaFormData.newBillingClientName,
              gpAccountingClientId: tpaFormData.gpAccountingClientId,
            },
            createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          };
          try {
            await dataProvider.create('TpaTransition', { data: payload });
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.TPA_TRANSITION_SAVED', { type: 'success' });
          } catch (error) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.TPA_TRANSITION_SAVE_ERROR', { type: 'error' });
          }
          break;
        case 'combine':
          if (combineClients.length === 0) {
            notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.COMBINE.SELECT_ONE', { type: 'warning' });
            return;
          }
          payload = {
            originalClientNumber: selectedClient.client_number,
            splitType: splitType,
            effectiveDate: effectiveDate,
            details: {
              primaryClient: {
                client_name: selectedClient.client_name,
                client_number: selectedClient.client_number,
              },
              combinedClients: combineClients.map((c) => ({
                client_name: c.client_name,
                client_number: c.client_number,
              })),
            },
            createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          };
          try {
            const { data } = await dataProvider.create('ClientCombineConfig', { data: payload });
            if (data.success) {
              notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.COMBINE_SAVED', { type: 'success' });

              setCombineClients([]);
            } else {
              notify('notifications.CONFIG_FAILED', { type: 'error' });
            }
          } catch (error) {
            notify('notifications.CONFIG_FAILED', { type: 'error' });
          } finally {
            setIsSaving(false);
          }
          break;
        case 'cleanup':
          const rulesToSave = rulesFromChild ?? cleanupRules;
          if (rulesToSave.length === 0) {
            notify('notifications.CLIENT_CLEANUP.RULES.ADD_ONE_RULE', { type: 'warning' });
            return;
          }
          payload = {
            originalClientNumber: selectedClient.client_number,
            clientName: selectedClient.client_name,
            splitType: 'cleanup',
            effectiveDate: effectiveDate,
            rules: rulesToSave,
            createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          };

          try {
            const res = await dataProvider.create('ClientCleanupConfig', { data: payload });
            if (res?.data?.success) {
              notify('notifications.CLIENT_CLEANUP.SAVED', { type: 'success' });
            } else {
              notify('notifications.CONFIG_FAILED', { type: 'error' });
            }
          } catch (error) {
            notify('notifications.CONFIG_FAILED', { type: 'error' });
          }
          break;
        case 'split':
          const splitRules = rulesFromChild ?? [];
          if (splitRules.length === 0) {
            notify('notifications.CLIENT_SPLIT.PREVIEW.MUST_ADD_ONE_RULE', { type: 'warning' });
            return;
          }

          const splitPayload = {
            originalClientNumber: selectedClient.client_number,
            originalClientName: selectedClient.client_name,
            splitType: 'split',
            effectiveDate: effectiveDate,
            createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
            rules: splitRules,
          };

          try {
            const res = await dataProvider.create('SaveClientSplit', { data: splitPayload });

            if (res?.data?.success) {
              notify('notifications.CLIENT_SPLIT.SAVE.SUCCESS', { type: 'success' });
            } else {
              notify('notifications.CLIENT_SPLIT.SAVE.FAILED', { type: 'error' });
            }
          } catch (error: any) {
            notify('notifications.CLIENT_SPLIT.SAVE.FAILED', { type: 'error' });
          }
          break;
      }
    } catch (error) {
      notify('notifications.CLIENT_SPLIT_NOTIFICATIONS.UNEXPECTED_ERROR', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };
  const uniqueClientChoices = Array.from(new Map(clientChoices.map((item) => [item.client_number, item])).values());
  const filteredOptions = uniqueClientChoices.filter(
    (option) =>
      (option.client_name && option.client_name.toLowerCase().includes(inputValue.toLowerCase())) ||
      (option.client_number && option.client_number.toLowerCase().includes(inputValue.toLowerCase()))
  );
  return (
    <Box sx={{ p: 2 }}>
      <Title title={CLIENT_SPLIT_STRINGS.PAGE_TITLE} />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
        <FormControl sx={{ width: 350 }}>
          <Autocomplete
            options={filteredOptions}
            value={selectedClient}
            onChange={(event, newValue) => setSelectedClient(newValue)}
            getOptionLabel={(option) =>
              `${option.client_name}${
                option.client_number
                  ? ` (${option.client_number})`
                  : ` ${MEMBER_SUMMARY_STRINGS.CLIENT_ID_MISSING_LABEL}`
              }`
            }
            isOptionEqualToValue={(option, value) => option.client_number === value.client_number}
            renderOption={(props, option) => (
              <li {...props} key={`${option.client_number}-${option.client_name}`}>
                {`${option.client_name} (${option.client_number})`}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={CLIENT_SPLIT_STRINGS.MAIN_SCREEN.CLIENT_SELECTOR_LABEL}
                variant="standard"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            loading={isLoading}
            filterOptions={(x) => x}
          />
        </FormControl>
        <FormControl sx={{ width: 250 }} variant="standard" size="small">
          <InputLabel id="split-type-select-label">{CLIENT_SPLIT_STRINGS.MAIN_SCREEN.SPLIT_TYPE_LABEL}</InputLabel>
          <Select
            labelId="split-type-select-label"
            value={splitType}
            label={CLIENT_SPLIT_STRINGS.MAIN_SCREEN.SPLIT_TYPE_LABEL}
            onChange={(event) => setSplitType(event.target.value)}
          >
            {splitTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={CLIENT_SPLIT_STRINGS.MAIN_SCREEN.EFFECTIVE_DATE_LABEL}
          type="date"
          value={effectiveDate}
          onChange={(event) => setEffectiveDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="standard"
          size="small"
          sx={{ width: 400 }}
        />
      </Box>
      {selectedClient && splitType === 'tpa' && effectiveDate && (
        <TpaTransitionForm
          formData={tpaFormData}
          handleInputChange={handleTpaFormChange}
          tpaList={tpaList}
          isTpaLoading={isTpaLoading}
          associatedTpa={associatedTpa}
          onTpaSelect={handleTpaSelect}
        />
      )}
      {selectedClient && splitType === 'split' && effectiveDate && (
        <ClientSplitForm
          primaryClient={selectedClient}
          effectiveDate={effectiveDate}
          onSaveRule={(rules) => {
            return handleSave(rules);
          }}
          isSaving={isSaving}
        />
      )}
      {selectedClient && splitType === 'combine' && effectiveDate && (
        <ClientCombineForm
          primaryClient={selectedClient}
          allClients={uniqueClientChoices}
          selectedCombineClients={combineClients}
          onCombineClientsChange={setCombineClients}
        />
      )}
      {selectedClient && splitType === 'cleanup' && effectiveDate && (
        <ClientCleanupForm
          primaryClient={selectedClient}
          effectiveDate={effectiveDate}
          onSaveRule={handleCleanupSave}
          isSaving={isSaving}
        />
      )}
      {selectedClient && (splitType === 'tpa' || splitType === 'combine') && effectiveDate && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={
              isSaving ||
              (splitType === 'combine' && combineClients.length === 0) ||
              (splitType === 'tpa' && (!tpaFormData.clientName || !tpaFormData.oldTpaName || !tpaFormData.newTpaName))
            }
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : CLIENT_SPLIT_STRINGS.MAIN_SCREEN.SAVE_BUTTON}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ManageClientSplit;
